import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { notificationsAPI } from '@/lib/api';
import { enablePush, pushPermission, refreshPushSubscription } from '@/lib/push';

const timeAgo = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} h ago`;
  return new Date(iso).toLocaleDateString('en-IN');
};

export const NotificationBell = ({ testPrefix = "notification" }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [perm, setPerm] = useState(pushPermission());

  const load = useCallback(() => notificationsAPI.list().then(r => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    refreshPushSubscription();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) await notificationsAPI.read(n.id).catch(() => {});
    load();
    navigate(n.url);
  };

  const turnOnPopups = async () => {
    try {
      await enablePush();
      setPerm('granted');
      toast.success('Pop-up notifications turned on for this device');
    } catch (e) {
      setPerm(pushPermission());
      toast.error(e.message);
    }
  };

  const readAll = async () => {
    await notificationsAPI.readAll().catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-md hover:bg-muted transition-colors" aria-label="Notifications" data-testid={`${testPrefix}-bell`}>
          {data.unread > 0 ? <BellRing className="w-5 h-5 text-primary" /> : <Bell className="w-5 h-5 text-muted-foreground" />}
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`${testPrefix}-unread-count`}>{data.unread > 99 ? '99+' : data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid={`${testPrefix}-panel`}>
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-sm font-bold">Notifications</span>
          {data.unread > 0 && <button onClick={readAll} className="text-xs text-primary flex items-center gap-1 hover:underline" data-testid={`${testPrefix}-read-all`}><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
        </div>
        {perm !== 'granted' && (
          <button onClick={turnOnPopups} className="w-full text-left px-3 py-2 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-b border-border" data-testid={`${testPrefix}-enable-push-btn`}>
            Turn on pop-up alerts on this device
          </button>
        )}
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No notifications yet</p>}
          {data.items.map(n => (
            <button key={n.id} onClick={() => openItem(n)} className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted transition-colors ${n.read ? '' : 'bg-primary/5'}`} data-testid={`${testPrefix}-item-${n.id}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{n.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
